import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import FormNumberInput from "go-form/components/FormNumberInput";
import { useCustomErrors } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { adminApi } from "../../../../../../../../../services/Api/Admin/adminApi";
import { GoLauncherCategoryApi } from "../../../../../../../../../services/Api/Admin/adminTypes";

interface Props {
	category?: GoLauncherCategoryApi;
}

const GoLauncherCategoryForm = ({ category }: Props) => {
	const form = useForm<GoLauncherCategoryApi>({
		criteriaMode: "all",
		defaultValues: category,
	});
	const {
		formState,
		formState: { errors },
		register,
		setError,
		watch,
		control,
		handleSubmit,
		reset,
	} = form;
	const { t } = useTranslation("admin");
	const { addFlash, addSuccessFlash } = useFlash();
	const [loading, setLoading] = useState(false);
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const textInputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;
	const numberInputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;
	const history = useHistory();

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) return;

		setLoading(true);
		try {
			let res: GoLauncherCategoryApi;
			if (category?.id) {
				res = await adminApi.updateGoLauncherCategory(category.id, data);
				reset(res);
			} else {
				res = await adminApi.createGoLauncherCategory(data);
				history.push(`/admin/go_launcher/categories/${res.id}`);
			}
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<FormDirty formState={formState} loading={loading} onSubmit={onSubmit}>
			<FormInput
				name="name"
				errors={errors}
				register={register}
				label={t("lib:common.word.name")}
				customValidationConfig={textInputValidationConfig}
			/>
			<FormInput
				name="description"
				errors={errors}
				register={register}
				label={t("lib:common.word.description")}
				as="textarea"
			/>
			<FormNumberInput
				errors={errors}
				name="position"
				control={control}
				label={t("modules.go_launcher.field.position.title")}
				customValidationConfig={numberInputValidationConfig}
				suffix=""
				allowNegative={false}
			/>
		</FormDirty>
	);
};

export default GoLauncherCategoryForm;
